import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Background images - user provided farmhouse bedroom
const BACKGROUNDS = {
  lampOn: "https://customer-assets.emergentagent.com/job_stark-fan-sleep/artifacts/yylpjkzd_ChatGPT%20Image%20Mar%2030%2C%202026%2C%2011_27_57%20AM.png",
  lampOff: "https://customer-assets.emergentagent.com/job_stark-fan-sleep/artifacts/ycoayng4_ChatGPT%20Image%20Mar%2030%2C%202026%2C%2011_28_52%20AM.png"
};

// Fan sound served from our backend (no CORS issues)
const FAN_SOUND_URL = `${API_URL}/api/audio/fan`;

// Speed settings
const SPEED_SETTINGS = {
  0: { label: "OFF", volume: 0, dialRotation: 0 },
  1: { label: "SPEED 1", volume: 0.3, dialRotation: 90 },
  2: { label: "SPEED 2", volume: 0.6, dialRotation: 180 },
  3: { label: "SPEED 3", volume: 1.0, dialRotation: 270 }
};

// Fan audio hook - gapless looping via Web Audio API buffer (same-origin, no CORS)
const useFanAudio = (speed) => {
  const audioContextRef = useRef(null);
  const bufferRef = useRef(null);
  const sourceRef = useRef(null);
  const gainRef = useRef(null);
  const isPlayingRef = useRef(false);
  const pendingSpeedRef = useRef(speed);
  pendingSpeedRef.current = speed;

  // Trim silence from start/end of decoded buffer for seamless looping
  const trimBuffer = useCallback((audioContext, buffer) => {
    const channel = buffer.getChannelData(0);
    const threshold = 0.01;
    let start = 0;
    let end = channel.length - 1;

    // Find first non-silent sample
    while (start < channel.length && Math.abs(channel[start]) < threshold) start++;
    // Find last non-silent sample
    while (end > start && Math.abs(channel[end]) < threshold) end--;

    // Add small margin
    start = Math.max(0, start - 100);
    end = Math.min(channel.length - 1, end + 100);

    const trimmedLength = end - start + 1;
    const trimmed = audioContext.createBuffer(
      buffer.numberOfChannels,
      trimmedLength,
      buffer.sampleRate
    );
    for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
      const src = buffer.getChannelData(ch);
      const dst = trimmed.getChannelData(ch);
      for (let i = 0; i < trimmedLength; i++) {
        dst[i] = src[start + i];
      }
    }
    return trimmed;
  }, []);

  const startSource = useCallback(() => {
    if (!audioContextRef.current || !bufferRef.current || isPlayingRef.current) return;
    sourceRef.current = audioContextRef.current.createBufferSource();
    sourceRef.current.buffer = bufferRef.current;
    sourceRef.current.loop = true;
    // Set loop points slightly inward to avoid any edge artifacts
    sourceRef.current.loopStart = 0.05;
    sourceRef.current.loopEnd = bufferRef.current.duration - 0.05;
    sourceRef.current.connect(gainRef.current);
    sourceRef.current.start(0, 0.05);
    isPlayingRef.current = true;
  }, []);

  const stopSource = useCallback(() => {
    if (sourceRef.current && isPlayingRef.current) {
      try { sourceRef.current.stop(); } catch(e) {}
      sourceRef.current.disconnect();
      sourceRef.current = null;
      isPlayingRef.current = false;
    }
  }, []);

  const initAudio = useCallback(() => {
    if (audioContextRef.current) return;
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContext();
      gainRef.current = audioContextRef.current.createGain();
      gainRef.current.gain.value = 0;
      gainRef.current.connect(audioContextRef.current.destination);

      fetch(FAN_SOUND_URL, { credentials: 'include' })
        .then(res => res.arrayBuffer())
        .then(data => audioContextRef.current.decodeAudioData(data))
        .then(decoded => {
          // Trim silence for seamless loop
          bufferRef.current = trimBuffer(audioContextRef.current, decoded);
          const vol = SPEED_SETTINGS[pendingSpeedRef.current].volume;
          if (vol > 0) {
            gainRef.current.gain.value = vol;
            startSource();
          }
        })
        .catch(e => console.error("Audio load failed:", e));
    } catch(e) {
      console.error("AudioContext failed:", e);
    }
  }, [startSource, trimBuffer]);

  useEffect(() => {
    if (!audioContextRef.current || !gainRef.current) return;
    const vol = SPEED_SETTINGS[speed].volume;
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    gainRef.current.gain.setTargetAtTime(vol, audioContextRef.current.currentTime, 0.1);
    if (speed > 0 && bufferRef.current) {
      startSource();
    } else if (speed === 0) {
      stopSource();
    }
  }, [speed, startSource, stopSource]);

  useEffect(() => {
    return () => {
      stopSource();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopSource]);

  return { initAudio };
};

// Desktop Oscillating Fan Component
const DesktopFan = ({ speed }) => {
  return (
    <div className="desktop-fan-container" data-testid="desktop-fan">
      <div className={`desktop-fan-head ${speed > 0 ? 'oscillating' : ''}`}>
        <div className="desktop-fan-cage">
          <div className="desktop-fan-hub" />
        </div>
        <div className="desktop-fan-grill" />
      </div>
      <div className="desktop-fan-neck" />
      <div className="desktop-fan-base" />
    </div>
  );
};

// Rotary Switch Component
const RotarySwitch = ({ speed, onSpeedChange }) => {
  const settings = SPEED_SETTINGS[speed];

  return (
    <div className="rotary-wrapper">
      {/* Speed buttons - the ONLY way to change speed */}
      <div className="speed-markers-outer">
        <button 
          type="button"
          className={`speed-btn speed-btn-off ${speed === 0 ? 'active' : ''}`}
          onClick={() => onSpeedChange(0)}
          data-testid="speed-btn-off"
        >OFF</button>
        <button 
          type="button"
          className={`speed-btn speed-btn-1 ${speed === 1 ? 'active' : ''}`}
          onClick={() => onSpeedChange(1)}
          data-testid="speed-btn-1"
        >1</button>
        <button 
          type="button"
          className={`speed-btn speed-btn-2 ${speed === 2 ? 'active' : ''}`}
          onClick={() => onSpeedChange(2)}
          data-testid="speed-btn-2"
        >2</button>
        <button 
          type="button"
          className={`speed-btn speed-btn-3 ${speed === 3 ? 'active' : ''}`}
          onClick={() => onSpeedChange(3)}
          data-testid="speed-btn-3"
        >3</button>
      </div>
      
      {/* Dial container - visual only, no click handler */}
      <div className="rotary-container" data-testid="rotary-switch">
        <div className="rotary-track" />
        <div 
          className="rotary-dial"
          style={{ transform: `translate(-50%, -50%) rotate(${settings.dialRotation}deg)` }}
          data-testid="dial-image"
        >
          <div className="rotary-indicator" />
        </div>
      </div>
    </div>
  );
};

// --- Login Page ---
const LoginPage = () => {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="farmhouse-app" data-testid="login-page">
      <div 
        className="background-layer"
        style={{ 
          backgroundImage: `url(${BACKGROUNDS.lampOn})`,
          opacity: 1 
        }}
      />
      <div className="farmhouse-content">
        <div className="mb-6 text-center">
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-amber-50 drop-shadow-lg tracking-tight">
            Bedtime Fan
          </h1>
          <p className="font-mono text-xs tracking-widest uppercase text-amber-200/60 mt-2">
            Sweet Dreams
          </p>
        </div>
        <div className="login-card">
          <p className="font-mono text-sm text-amber-200/80 mb-6 text-center">
            Sign in to use the Bedtime Fan
          </p>
          <button 
            onClick={handleLogin} 
            className="google-login-btn"
            data-testid="google-login-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" className="mr-3">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Not Approved Page ---
const NotApprovedPage = ({ user, onLogout }) => {
  return (
    <div className="farmhouse-app" data-testid="not-approved-page">
      <div 
        className="background-layer"
        style={{ 
          backgroundImage: `url(${BACKGROUNDS.lampOn})`,
          opacity: 1 
        }}
      />
      <div className="farmhouse-content">
        <div className="mb-6 text-center">
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-amber-50 drop-shadow-lg tracking-tight">
            Bedtime Fan
          </h1>
        </div>
        <div className="login-card">
          <div className="text-center">
            {user.picture && (
              <img src={user.picture} alt="" className="w-16 h-16 rounded-full mx-auto mb-4" />
            )}
            <p className="font-mono text-sm text-amber-200/80 mb-2">
              Hi {user.name || user.email},
            </p>
            <p className="font-mono text-sm text-amber-200/60 mb-6">
              Your account is pending approval. Please contact the admin.
            </p>
            <button 
              onClick={onLogout}
              className="logout-btn"
              data-testid="logout-btn"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Admin Panel ---
const AdminPanel = ({ onClose, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (e) {
      console.error("Failed to fetch users:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleApprove = async (userId) => {
    await fetch(`${API_URL}/api/admin/users/${userId}/approve`, {
      method: 'POST', credentials: 'include'
    });
    fetchUsers();
  };

  const handleDeny = async (userId) => {
    await fetch(`${API_URL}/api/admin/users/${userId}/deny`, {
      method: 'POST', credentials: 'include'
    });
    fetchUsers();
  };

  const handleRemove = async (userId) => {
    await fetch(`${API_URL}/api/admin/users/${userId}/remove`, {
      method: 'POST', credentials: 'include'
    });
    fetchUsers();
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    await fetch(`${API_URL}/api/admin/users/add`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: newEmail.trim() })
    });
    setNewEmail("");
    fetchUsers();
  };

  return (
    <div className="admin-overlay" data-testid="admin-panel">
      <div className="admin-panel">
        <div className="admin-header">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-amber-50">
            Manage Users
          </h2>
          <button onClick={onClose} className="admin-close-btn" data-testid="admin-close-btn">
            &times;
          </button>
        </div>

        {/* Add user form */}
        <form onSubmit={handleAddEmail} className="admin-add-form">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Pre-approve email..."
            className="admin-input"
            data-testid="admin-add-email-input"
          />
          <button type="submit" className="admin-add-btn" data-testid="admin-add-email-btn">
            Add
          </button>
        </form>

        {/* Users list */}
        <div className="admin-users-list">
          {loading ? (
            <p className="font-mono text-sm text-amber-200/60 text-center py-4">Loading...</p>
          ) : users.length === 0 ? (
            <p className="font-mono text-sm text-amber-200/60 text-center py-4">No users yet</p>
          ) : (
            users.map((u) => (
              <div key={u.user_id} className="admin-user-row" data-testid={`user-row-${u.user_id}`}>
                <div className="admin-user-info">
                  <div className="flex items-center gap-2">
                    {u.picture && (
                      <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                    )}
                    <div>
                      <p className="font-mono text-sm text-amber-50">
                        {u.name || u.email}
                        {u.role === 'admin' && <span className="admin-badge">ADMIN</span>}
                      </p>
                      <p className="font-mono text-xs text-amber-200/50">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`status-dot ${u.has_active_session ? 'online' : 'offline'}`} />
                    <span className={`approval-badge ${u.approved ? 'approved' : 'pending'}`}>
                      {u.approved ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                </div>
                {u.user_id !== currentUser.user_id && (
                  <div className="admin-user-actions">
                    {!u.approved ? (
                      <button onClick={() => handleApprove(u.user_id)} className="action-btn approve-btn" data-testid={`approve-${u.user_id}`}>
                        Approve
                      </button>
                    ) : (
                      <button onClick={() => handleDeny(u.user_id)} className="action-btn deny-btn" data-testid={`deny-${u.user_id}`}>
                        Revoke
                      </button>
                    )}
                    <button onClick={() => handleRemove(u.user_id)} className="action-btn remove-btn" data-testid={`remove-${u.user_id}`}>
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// --- Main Fan App ---
const FanApp = ({ user, onLogout }) => {
  const [speed, setSpeed] = useState(0);
  const [showAdmin, setShowAdmin] = useState(false);
  const { initAudio } = useFanAudio(speed);
  
  const handleSpeedChange = (newSpeed) => {
    initAudio();
    setSpeed(newSpeed);
  };
  
  const isLampOn = speed === 0;
  const settings = SPEED_SETTINGS[speed];
  const isAdmin = user.role === 'admin';

  return (
    <div className="farmhouse-app" data-testid="farmhouse-fan-app">
      {/* Background layers */}
      <div 
        className="background-layer"
        style={{ 
          backgroundImage: `url(${BACKGROUNDS.lampOff})`,
          opacity: isLampOn ? 0 : 1 
        }}
      />
      <div 
        className="background-layer"
        style={{ 
          backgroundImage: `url(${BACKGROUNDS.lampOn})`,
          opacity: isLampOn ? 1 : 0 
        }}
      />
      
      {/* Top bar - user info + admin button */}
      <div className="top-bar" data-testid="top-bar">
        <div className="top-bar-left">
          {user.picture && (
            <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
          )}
          <span className="font-mono text-xs text-amber-200/70">{user.name || user.email}</span>
        </div>
        <div className="top-bar-right">
          {isAdmin && (
            <button 
              onClick={() => setShowAdmin(true)} 
              className="admin-top-btn"
              data-testid="admin-btn"
            >
              ADMIN
            </button>
          )}
          <button 
            onClick={onLogout} 
            className="logout-top-btn"
            data-testid="logout-btn"
          >
            Sign Out
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="farmhouse-content">
        <div className="mb-1 md:mb-6 text-center">
          <h1 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold text-amber-50 drop-shadow-lg tracking-tight">
            Bedtime Fan
          </h1>
          <p className="font-mono text-sm tracking-widest uppercase text-amber-200/80 mt-1 drop-shadow-lg">
            Sweet Dreams
          </p>
        </div>
        
        
        <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-16 w-full flex-1 md:flex-initial">
          <div className="flex flex-col items-center self-start ml-2 md:self-center md:ml-0 order-1 md:-mt-96 md:-ml-20 mt-4 md:mt-0">
            <DesktopFan speed={speed} />
            <p className="mt-1 md:mt-4 font-mono text-xs tracking-wider text-amber-200/60 uppercase">
              {speed > 0 ? settings.label : 'Fan Off'}
            </p>
          </div>
          
          <div className="py-2 px-4 md:py-12 md:px-8 order-2 mt-16 md:mt-0">
            <RotarySwitch speed={speed} onSpeedChange={handleSpeedChange} />
          </div>
        </div>
        
        <p className="mt-6 font-mono text-xs tracking-wider text-amber-200/40 uppercase">
          Click OFF, 1, 2, or 3 to change speed
        </p>
      </div>

      {/* Admin Panel Modal */}
      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} currentUser={user} />}
    </div>
  );
};

// --- Auth Callback (processes session_id from URL fragment) ---
const AuthCallback = ({ onAuth }) => {
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      window.location.href = '/';
      return;
    }

    const sessionId = match[1];

    fetch(`${API_URL}/api/auth/session`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId })
    })
      .then(res => {
        if (!res.ok) throw new Error('Auth failed');
        return res.json();
      })
      .then(userData => {
        // Clear hash and pass user data
        window.history.replaceState(null, '', '/');
        onAuth(userData);
      })
      .catch(() => {
        window.history.replaceState(null, '', '/');
        onAuth(null);
      });
  }, [onAuth]);

  return (
    <div className="farmhouse-app">
      <div className="background-layer" style={{ backgroundImage: `url(${BACKGROUNDS.lampOn})`, opacity: 1 }} />
      <div className="farmhouse-content">
        <p className="font-mono text-sm text-amber-200/80">Signing in...</p>
      </div>
    </div>
  );
};

// --- Main App Component ---
function App() {
  const [user, setUser] = useState(null);
  const [authState, setAuthState] = useState('checking'); // checking | login | callback | approved | not_approved

  // Check if returning from OAuth with session_id in hash
  const hasSessionId = window.location.hash?.includes('session_id=');

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setAuthState('callback');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
      if (!res.ok) throw new Error('Not authenticated');
      const userData = await res.json();
      setUser(userData);
      setAuthState(userData.approved ? 'approved' : 'not_approved');
    } catch {
      setAuthState('login');
    }
  }, []);

  useEffect(() => {
    if (!hasSessionId) {
      checkAuth();
    } else {
      setAuthState('callback');
    }
  }, [checkAuth, hasSessionId]);

  const handleAuth = useCallback((userData) => {
    if (userData) {
      setUser(userData);
      setAuthState(userData.approved ? 'approved' : 'not_approved');
    } else {
      setAuthState('login');
    }
  }, []);

  const handleLogout = async () => {
    await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(null);
    setAuthState('login');
  };

  if (authState === 'checking') {
    return (
      <div className="farmhouse-app">
        <div className="background-layer" style={{ backgroundImage: `url(${BACKGROUNDS.lampOn})`, opacity: 1 }} />
        <div className="farmhouse-content">
          <p className="font-mono text-sm text-amber-200/80">Loading...</p>
        </div>
      </div>
    );
  }

  if (authState === 'callback') {
    return <AuthCallback onAuth={handleAuth} />;
  }

  if (authState === 'login') {
    return <LoginPage />;
  }

  if (authState === 'not_approved') {
    return <NotApprovedPage user={user} onLogout={handleLogout} />;
  }

  return <FanApp user={user} onLogout={handleLogout} />;
}

export default App;
